// BattlePage.js
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { auth } from './firebase';
import {
  Swords, Trophy, Crown, RotateCcw,
  WifiOff, CheckCircle, XCircle, Zap, TrendingUp, TrendingDown,
} from 'lucide-react';
import BattleArena from './BattleArena';
import MatchmakingOverlay from './MatchmakingOverlay';
import { useBattle, PHASE } from './hooks/useBattle';
import { Avatar, getTier } from './battleUtils';
import Sidebar from './Sidebar';
import './BattlePage.css';

// ─── ELO change pill ──────────────────────────────────────────────────────────
const EloDelta = ({ eloData }) => {
  if (!eloData) return null;
  const gain = eloData.delta >= 0;
  return (
    <div className={`bp-elo-delta ${gain ? 'gain' : 'loss'}`}>
      {gain ? <TrendingUp size={13} /> : <TrendingDown size={13} />}
      <span className="bp-elo-delta-label">ELO</span>
      <span className="bp-elo-delta-old">{eloData.oldElo}</span>
      <span className="bp-elo-delta-arrow">→</span>
      <span className="bp-elo-delta-new">{eloData.newElo}</span>
      <span className={`bp-elo-delta-badge ${gain ? 'gain' : 'loss'}`}>
        {gain ? '+' : ''}{eloData.delta}
      </span>
    </div>
  );
};

// ─── Score card — big visual hero card ───────────────────────────────────────
const ScoreCard = ({ username, score, isWinner, minimumScore, eloData, isMe }) => {
  const passed = score >= (minimumScore || 75);
  return (
    <div className={`bp-score-card ${isWinner ? 'winner' : 'loser'} ${isMe ? 'is-me' : ''}`}>
      {isWinner && (
        <div className="bp-score-card-crown">
          <Crown size={14} /> Winner
        </div>
      )}
      <div className="bp-score-card-avatar">
        <Avatar seed={username} size={56} />
        {isWinner && <span className="bp-score-card-avatar-ring" />}
      </div>
      <div className="bp-score-card-name">{username}</div>
      <div className={`bp-score-card-score ${passed ? 'pass' : 'fail'}`}>
        {score}
        <span className="bp-score-card-max">/100</span>
      </div>
      <div className={`bp-score-card-status ${passed ? 'pass' : 'fail'}`}>
        {passed ? '✓ Passed' : '✗ Below threshold'}
      </div>
      <EloDelta eloData={eloData} />
    </div>
  );
};

// ─── Score breakdown panel ────────────────────────────────────────────────────
const ScoreBreakdown = ({ details, minimumScore }) => {
  if (!details) return null;
  const { scoring, effectiveness, feedback, breakdown } = details;
  const effDims = [
    { key: 'clarity',     label: 'Clarity' },
    { key: 'specificity', label: 'Specificity' },
    { key: 'usability',   label: 'Usability' },
    { key: 'concise',     label: 'Concise' },
  ];
  return (
    <div className="bp-breakdown">
      {breakdown?.formula && (
        <div className="bp-breakdown-formula">
          <Zap size={13} />{breakdown.formula}
        </div>
      )}
      {scoring?.length > 0 && (
        <div className="bp-breakdown-section">
          <div className="bp-breakdown-title">Rule Compliance</div>
          <div className="bp-rules-list">
            {scoring.map(rule => (
              <div key={rule.number} className={`bp-rule-row ${rule.passed ? 'pass' : 'fail'}`}>
                <div className="bp-rule-top">
                  {rule.passed
                    ? <CheckCircle size={14} className="bp-rule-icon pass" />
                    : <XCircle    size={14} className="bp-rule-icon fail" />}
                  <span className="bp-rule-label">{rule.label}</span>
                  <span className={`bp-rule-score ${rule.passed ? 'pass' : 'fail'}`}>{rule.score}/100</span>
                </div>
                {!rule.passed && rule.tip && <p className="bp-rule-tip">💡 {rule.tip}</p>}
              </div>
            ))}
          </div>
        </div>
      )}
      {effectiveness && (
        <div className="bp-breakdown-section">
          <div className="bp-breakdown-title">Prompt Effectiveness</div>
          <div className="bp-eff-grid">
            {effDims.map(({ key, label }) => {
              const val = effectiveness[key] ?? 0;
              const ok  = val >= 60;
              return (
                <div key={key} className={`bp-eff-cell ${ok ? 'ok' : 'low'}`}>
                  <span className="bp-eff-score">{val}</span>
                  <span className="bp-eff-label">{label}</span>
                  <div className="bp-eff-bar">
                    <div className="bp-eff-fill" style={{ width: `${val}%`, background: ok ? '#10B981' : '#EF4444' }} />
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}
      {feedback && (
        <div className="bp-feedback">
          <span className="bp-feedback-label">Key Feedback</span>
          <p>{feedback}</p>
        </div>
      )}
    </div>
  );
};

// ─── MAIN PAGE ────────────────────────────────────────────────────────────────
const BattlePage = () => {
  const navigate = useNavigate();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const {
    phase, timeLeft, challenge, constraints, minimumScore,
    opponent, userPrompt, setUserPrompt,
    isSubmitting, myScore, myScoreDetails, opponentSubmitted,
    result, error, eloData, oppEloData, myEntry,
    joinQueue, cancelQueue, submitBattle, resetBattle,
    // ── new: challenge-a-friend ──
    challengeCode, challengeError, createChallenge, cancelChallenge,
  } = useBattle();

  const user       = auth.currentUser;
  const myUid      = user?.uid;
  const myUsername = user?.displayName || user?.email?.split('@')[0] || 'You';

  const myResultInBattle  = result?.players?.find(p => p.userId === myUid);
  const oppResultInBattle = result?.players?.find(p => p.userId !== myUid);
  const iWon              = result?.winnerId === myUid;

  return (
    <div className="bp-shell">
      <Sidebar sidebarOpen={sidebarOpen} setSidebarOpen={setSidebarOpen} />
      {sidebarOpen && <div className="bp-overlay" onClick={() => setSidebarOpen(false)} />}

      {(phase === PHASE.IDLE || phase === PHASE.QUEUING) && (
        <MatchmakingOverlay
          phase={phase}
          myUsername={myUsername}
          myEntry={myEntry}
          joinQueue={joinQueue}
          cancelQueue={cancelQueue}
          navigate={navigate}
          user={user}
          challengeCode={challengeCode}
          challengeError={challengeError}
          createChallenge={createChallenge}
          cancelChallenge={cancelChallenge}
        />
      )}

      <div className={`bp-main ${sidebarOpen ? 'sidebar-is-open' : ''}`}>

        <button className="bp-hamburger" onClick={() => setSidebarOpen(v => !v)} title="Toggle menu">
          <span /><span /><span />
        </button>

        {error && (
          <div className="bp-error-bar">
            <WifiOff size={14} /> {error}
          </div>
        )}

        {/* ── BATTLE phase ── */}
        {phase === PHASE.BATTLE && challenge && (
          <BattleArena
            challenge={challenge}
            constraints={constraints}
            minimumScore={minimumScore}
            timeLeft={timeLeft}
            myUsername={myUsername}
            myEntry={myEntry}
            opponent={opponent}
            userPrompt={userPrompt}
            setUserPrompt={setUserPrompt}
            isSubmitting={isSubmitting}
            myScore={myScore}
            opponentSubmitted={opponentSubmitted}
            submitBattle={submitBattle}
            sidebarOpen={sidebarOpen}
          />
        )}

        {/* ── IDLE / QUEUING placeholder ── */}
        {(phase === PHASE.IDLE || phase === PHASE.QUEUING) && (
          <div className="bp-two-panels">
            <div className="bp-panel bp-panel-left">
              <div className="bp-column-header">
                <Swords size={18} className="bp-column-icon" />
                <h3>Battle Arena</h3>
              </div>
              <div className="bp-idle-body">
                <div className="bp-idle-inner">
                  <Swords size={48} className="bp-idle-icon" />
                  <p>Find a match to start battling</p>
                </div>
              </div>
            </div>
            <div className="bp-panel bp-panel-right">
              <div className="bp-column-header">
                <Trophy size={18} className="bp-column-icon" />
                <h3>Leaderboard & Stats</h3>
              </div>
              <div className="bp-idle-body">
                {myEntry && (
                  <div className="bp-my-elo-card">
                    <Avatar seed={myUsername} size={52} />
                    <div className="bp-elo-info">
                      <span className="bp-elo-name">{myUsername}</span>
                      <span className="bp-elo-score">{myEntry.elo} ELO</span>
                      <span className="bp-elo-tier" style={{ color: getTier(myEntry.elo).color }}>
                        {getTier(myEntry.elo).icon} {getTier(myEntry.elo).label}
                      </span>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}

        {/* ── RESULT phase ── */}
        {phase === PHASE.RESULT && result && (
          <div className="bp-two-panels">

            {/* LEFT — Your result */}
            <div className="bp-panel bp-panel-left">
              <div className="bp-column-header">
                <Trophy size={18} className="bp-column-icon" />
                <h3>Your Result</h3>
              </div>
              <div className="bp-result-body">

                <div className={`bp-result-banner ${iWon ? 'win' : 'lose'}`}>
                  {iWon ? <><Trophy size={20} /> You Won!</> : <>Better luck next time</>}
                </div>

                {result.reason === 'opponent_disconnected' && (
                  <p className="bp-result-note">Opponent disconnected — you win by default.</p>
                )}

                <ScoreCard
                  username={myUsername}
                  score={myResultInBattle?.score ?? 0}
                  isWinner={iWon}
                  minimumScore={minimumScore}
                  eloData={eloData}
                  isMe
                />

                <ScoreBreakdown details={myScoreDetails} minimumScore={minimumScore} />

                <div className="bp-prompt-side you">
                  <div className="bp-prompt-side-header">
                    <Avatar seed={myUsername} size={16} />
                    <span>Your prompt</span>
                  </div>
                  <p className="bp-prompt-side-text">
                    {myResultInBattle?.prompt ?? '(not submitted)'}
                  </p>
                </div>

                <div className="bp-result-actions">
                  <button className="bp-btn-primary" onClick={() => { resetBattle(); joinQueue(); }}>
                    <Swords size={14} /> Battle Again
                  </button>
                  <button className="bp-btn-secondary" onClick={resetBattle}>
                    <RotateCcw size={14} /> Back
                  </button>
                </div>

              </div>
            </div>

            {/* RIGHT — Opponent's result */}
            <div className="bp-panel bp-panel-right">
              <div className="bp-column-header">
                <Zap size={18} className="bp-column-icon" />
                <h3>{oppResultInBattle?.username ?? 'Opponent'}'s Result</h3>
              </div>
              <div className="bp-result-body">

                <div className={`bp-result-banner ${!iWon ? 'win' : 'lose'}`}>
                  {!iWon
                    ? <><Trophy size={20} /> {oppResultInBattle?.username} Won!</>
                    : <>Better luck next time</>}
                </div>

                <ScoreCard
                  username={oppResultInBattle?.username ?? '???'}
                  score={oppResultInBattle?.score ?? 0}
                  isWinner={!iWon}
                  minimumScore={minimumScore}
                  eloData={oppEloData}
                  isMe={false}
                />

                <ScoreBreakdown details={oppResultInBattle?.details} minimumScore={minimumScore} />

                <div className="bp-prompt-side opp">
                  <div className="bp-prompt-side-header">
                    <Avatar seed={oppResultInBattle?.username} size={16} />
                    <span>{oppResultInBattle?.username}'s prompt</span>
                  </div>
                  <p className="bp-prompt-side-text">
                    {oppResultInBattle?.prompt ?? '(not submitted)'}
                  </p>
                </div>

              </div>
            </div>

          </div>
        )}
      </div>
    </div>
  );
};

export default BattlePage;